import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { Box, Typography, Form, Loader } from '@lucid/lego';
import type { FormRef } from '@lucid/lego';
import { useLocaleNavigate } from '../../../hooks/useLocaleNavigate';
import { useStore } from '../../../store/store';
import { useCreateProspect } from '../../../api/services/lupp/prospect/prospect';
import type {
  CreateProspectRequest,
  CreateProspectResponse,
  TestDriveSchedule,
} from '../../../api/services/lupp/prospect/dto.prospect';
import { useGetLabels } from '../../../hooks/useLabels';
import { usePostalCodeLookup } from '../../../hooks/usePostalCodeLookup';
import { convertToContentfulLocale } from '../../../utils/tools';
import { createLeadFormFields } from './formFields';
import styles from './CreateNewProspect.module.scss';

const REDIRECT_DELAY = 3000;
const REDIRECT_PATH = '/prospect-desk/prospects';

/**
 * Formats a date with specific hours/minutes as ISO string with timezone offset
 */
const formatDateTimeISO = (date: Date, hours: number, minutes: number): string => {
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(hours)}:${pad(minutes)}:00+00:00`;
};

/**
 * Builds test drive schedule from form data
 * The Form component passes data keyed by apiKey, so we use those keys here
 */
const buildTestDriveSchedule = (
  formData: Record<string, unknown>,
  location: string
): TestDriveSchedule[] | undefined => {
  const isTestDrive = formData.isTestDrive as string[] | undefined;
  if (!isTestDrive?.includes('yes')) return undefined;

  const timeSlot = (formData.testDriveTime as string) || '9:00-9:30';
  const [startTime, endTime] = timeSlot.split('-');
  const testDriveDate = formData.testDriveDate as string | undefined;
  const baseDate = testDriveDate ? new Date(testDriveDate) : new Date();

  const [startHour, startMinute] = startTime.split(':').map(Number);
  const [endHour, endMinute] = endTime.split(':').map(Number);

  return [
    {
      appointmentLocationName: location,
      scheduledStartDatetime: formatDateTimeISO(baseDate, startHour, startMinute),
      scheduledEndDatetime: formatDateTimeISO(baseDate, endHour, endMinute),
      testDriveStatus: (formData.status as string) || 'scheduled',
      trim: (formData.selectedModelTrim as string) || '',
      isActive: true,
      isDeleted: false,
    },
  ];
};

const Create: React.FC = () => {
  const { userIdentity } = useStore();
  const theme = useStore((state) => state.theme);
  const locale = useStore((state) => state.locale);
  const navigate = useLocaleNavigate();
  const { mutate, isPending } = useCreateProspect();
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string>('');
  const [createdProspectId, setCreatedProspectId] = useState<string | null>(null);
  const [postalCode, setPostalCode] = useState<string>('');
  const messageRef = useRef<HTMLDivElement>(null);
  const formRef = useRef<FormRef>(null);

  const { data: labels } = useGetLabels({
    id: '6dhtpdHCYn1jTZ1cRAXtJR',
    locale: convertToContentfulLocale(locale),
  });

  // Postal code lookup for auto-populating city
  const { locationData } = usePostalCodeLookup(postalCode, {
    debounceMs: 500,
    enabled: postalCode.length >= 3,
  });

  // Store location data from HereMaps for use in payload
  const [hereLocationData, setHereLocationData] = useState<{
    city?: string;
    stateCode?: string;
    countryCode?: string;
  }>({});

  // Auto-populate city and country, store location data when available
  useEffect(() => {
    if (locationData?.city && formRef.current) {
      // Use the form ref's setValue method to properly update the field with validation
      formRef.current.setValue('city', locationData.city, {
        shouldValidate: true,
        shouldDirty: true,
      });

      if (locationData.country) {
        formRef.current.setValue('country', locationData.country);
      }

      // Store location data for use in payload
      setHereLocationData({
        city: locationData.city || undefined,
        stateCode: locationData.stateCode || undefined,
        countryCode: locationData.countryCode || undefined,
      });
    }
  }, [locationData]);

  const handleCancel = useCallback(() => {
    navigate(REDIRECT_PATH);
  }, [navigate]);

  const handlePostalCodeChange = useCallback((value: unknown) => {
    // Extract value from event if it's an event object, otherwise use the value directly
    if (value && typeof value === 'object' && 'target' in value) {
      const event = value as React.ChangeEvent<HTMLInputElement>;
      setPostalCode(event.target.value || '');
    } else {
      setPostalCode(String(value || ''));
    }
  }, []);

  const location = userIdentity?.addressLine1;
  const stateProvince = userIdentity?.stateProvince;
  const countryCode = userIdentity?.countryCode;
  const organizationName = userIdentity?.organizationName;

  const formFields = useMemo(
    () =>
      createLeadFormFields({
        onCancel: handleCancel,
        location,
        organizationName,
        onPostalCodeChange: handlePostalCodeChange,
      }),
    [handleCancel, location, organizationName, handlePostalCodeChange]
  );

  // Cleanup timeout on unmount to prevent memory leaks
  useEffect(() => {
    let timeoutId: NodeJS.Timeout;
    if (successMessage) {
      timeoutId = setTimeout(() => {
        // Navigate to the created prospect if we have an ID, otherwise go to prospects list
        const redirectPath = createdProspectId
          ? `/prospect-desk/prospects/${createdProspectId}`
          : REDIRECT_PATH;
        navigate(redirectPath);
      }, REDIRECT_DELAY);
    }
    return () => {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
    };
  }, [successMessage, navigate, createdProspectId]);

  // Scroll to message when error or success message appears
  useEffect(() => {
    if ((errorMessage || successMessage) && messageRef.current) {
      messageRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, [errorMessage, successMessage]);

  const handleSuccess = (data: unknown) => {
    setErrorMessage(null);
    // Form component's sanitizeData already maps field names to apiKeys
    // So data is already keyed by apiKey (e.g., prospectEmail, prospectFirstName, etc.)
    const formData = data as Record<string, unknown>;

    // Build the payload - Form already transformed field names to apiKeys
    // We only need to add the testDriveSchedules which requires custom logic
    const payload: CreateProspectRequest = {
      prospectFirstName: (formData.prospectFirstName as string) || '',
      prospectMiddleName: (formData.prospectMiddleName as string) || '',
      prospectLastName: (formData.prospectLastName as string) || '',
      prospectTitle: (formData.prospectTitle as string) || '',
      prospectEmail: (formData.prospectEmail as string) || '',
      prospectPhone: (formData.prospectPhone as string) || '',
      zipCode: (formData.zipCode as string) || '',
      city: (formData.city as string) || '',
      // Use HereMaps data if available, fallback to userIdentity
      stateProvince: hereLocationData.stateCode || stateProvince || '',
      countryCode: hereLocationData.countryCode || countryCode || '',
      interestedModel: (formData.interestedModel as string) || '',
      interestedVehicleCondition: (formData.interestedVehicleCondition as string) || '',
      currentCarMake: (formData.currentCarMake as string) || '',
      purchaseType: (formData.purchaseType as string) || '',
      financeType: (formData.financeType as string) || '',
      purchaseTiming: (formData.purchaseTiming as string) || '',
      preferredContactMethod: (formData.preferredContactMethod as string) || '',
      isEmailOptIn: (formData.isEmailOptIn as string) === 'emailOptIn',
      isCallOptIn: (formData.isCallOptIn as string) === 'callOptIn',
      noteText: (formData.noteText as string) || '',
      testDriveSchedules: buildTestDriveSchedule(formData, location || ''),
    };

    mutate(payload, {
      onSuccess: (response: CreateProspectResponse) => {
        if (response.prospectIdentifier) {
          setCreatedProspectId(response.prospectIdentifier);
        }
        setSuccessMessage(
          labels?.prospectCreatedSuccess || 'Prospect created successfully! Redirecting...'
        );
      },
      onError: (error: unknown) => {
        setSuccessMessage('');
        // API errors are returned as strings from the LUPP service
        const errorMsg =
          typeof error === 'string'
            ? error
            : error instanceof Error
              ? error.message
              : labels?.prospectCreateError || 'Failed to create prospect. Please try again.';
        setErrorMessage(errorMsg);
      },
    });
  };

  const handleError = () => {
    setErrorMessage(labels?.formValidationError || 'Please fix the form errors before submitting.');
    setSuccessMessage('');
  };

  return (
    <div ref={messageRef}>
      <Typography variant="headline" size="small" color="primary-1" theme={theme}>
        {labels?.createNewProspect || 'Create New Prospect'}
      </Typography>
      <Box pt={8}>
        <Typography variant="body" size="medium" color="gray-5" theme={theme}>
          {labels?.addNewProspectDescription || 'Add a new prospect to your sales funnel'}
        </Typography>
      </Box>
      <Box className={styles.formContent} background={theme === 'dark' ? 'white' : 'black'} mt={32}>
        <Box className={styles.formWrapper}>
          {successMessage && (
            <Box mb={16} pt={12} pb={12} pl={16} pr={16} className={styles.successMessageText}>
              <Typography variant="body" size="medium" color="white" theme={theme}>
                {successMessage}
              </Typography>
            </Box>
          )}
          {errorMessage && (
            <Box mb={16} pt={12} pb={12} pl={16} pr={16} className={styles.errorMessageText}>
              <Typography variant="body" size="medium" color="white" theme={theme}>
                {errorMessage}
              </Typography>
            </Box>
          )}
          {isPending && (
            <Box className="fetchingOverlay">
              <Loader size={32} />
            </Box>
          )}
          <Form
            id="create-prospect-form"
            ref={formRef}
            theme={theme === 'dark' ? 'light' : 'dark'}
            formFields={formFields}
            onSuccess={handleSuccess}
            onError={handleError}
            isFluid={true}
          />
        </Box>
      </Box>
    </div>
  );
};

export default Create;
